import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import './TagFilterDropdown.css';

/**
 * Searchable multi-select tag list.
 * Used standalone inside TagFilterDropdown and embedded in the filter rail's
 * Tags pill popover.
 */
export const TagFilterMenu = ({ tags, selectedTags, onChange, embedded = false, listId }) => {
    const [query, setQuery] = useState('');
    const searchRef = useRef(null);

    useEffect(() => {
        searchRef.current?.focus();
    }, []);

    const filteredTags = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return tags;
        return tags.filter((tag) => tag.toLowerCase().includes(needle));
    }, [tags, query]);

    const toggleTag = (tag) => {
        if (selectedTags.includes(tag)) {
            onChange(selectedTags.filter((t) => t !== tag));
        } else {
            onChange([...selectedTags, tag]);
        }
    };

    return (
        <div className={`tag-dropdown-menu${embedded ? ' tag-dropdown-menu--embedded' : ''}`}>
            <div className="tag-dropdown-search">
                <Search size={14} className="tag-dropdown-search-icon" aria-hidden="true" />
                <input
                    ref={searchRef}
                    type="text"
                    className="tag-dropdown-search-input"
                    placeholder="Search tags"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    aria-label="Search tags"
                />
                {query && (
                    <button
                        type="button"
                        className="tag-dropdown-search-clear"
                        onClick={() => setQuery('')}
                        aria-label="Clear search"
                    >
                        <X size={13} />
                    </button>
                )}
            </div>

            <div className="tag-dropdown-list" id={listId} role="listbox" aria-multiselectable="true">
                {filteredTags.length === 0 ? (
                    <div className="tag-dropdown-empty">No tags match “{query.trim()}”</div>
                ) : (
                    filteredTags.map((tag) => {
                        const checked = selectedTags.includes(tag);
                        return (
                            <label
                                key={tag}
                                className={`tag-dropdown-option${checked ? ' is-checked' : ''}`}
                                role="option"
                                aria-selected={checked}
                            >
                                <input
                                    type="checkbox"
                                    className="tag-dropdown-checkbox"
                                    checked={checked}
                                    onChange={() => toggleTag(tag)}
                                />
                                <span className="tag-dropdown-checkbox-box" aria-hidden="true">
                                    <Check size={11} strokeWidth={3} />
                                </span>
                                <span className="tag-dropdown-option-label" title={tag}>{tag}</span>
                            </label>
                        );
                    })
                )}
            </div>

            <div className="tag-dropdown-footer">
                <span className="tag-dropdown-footer-count">
                    {selectedTags.length} of {tags.length} selected
                </span>
                {selectedTags.length > 0 && (
                    <button type="button" className="tag-dropdown-footer-clear" onClick={() => onChange([])}>
                        Clear all
                    </button>
                )}
            </div>
        </div>
    );
};

/**
 * Multi-select dropdown for filtering by link tags (trigger + TagFilterMenu).
 */
const TagFilterDropdown = ({ tags, selectedTags, onChange, placeholder = 'None' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const rootRef = useRef(null);
    const listId = useId();

    // Close on outside click / Escape.
    useEffect(() => {
        if (!isOpen) return undefined;
        const handlePointer = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        const handleKey = (event) => {
            if (event.key === 'Escape') setIsOpen(false);
        };
        document.addEventListener('mousedown', handlePointer);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handlePointer);
            document.removeEventListener('keydown', handleKey);
        };
    }, [isOpen]);

    const clearAll = (event) => {
        event.stopPropagation();
        onChange([]);
    };

    const summary = selectedTags.length === 0
        ? placeholder
        : selectedTags.length === 1
            ? selectedTags[0]
            : `${selectedTags.length} tags selected`;

    return (
        <div className={`tag-filter-dropdown${isOpen ? ' is-open' : ''}`} ref={rootRef}>
            <button
                type="button"
                className="tag-filter-button"
                onClick={() => setIsOpen((prev) => !prev)}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-controls={listId}
            >
                <span className={`tag-filter-button-text${selectedTags.length === 0 ? ' is-placeholder' : ''}`}>
                    {summary}
                </span>
                <span className="tag-filter-button-actions">
                    {selectedTags.length > 0 && (
                        <span
                            role="button"
                            tabIndex={0}
                            className="tag-filter-clear"
                            aria-label="Clear selected tags"
                            title="Clear"
                            onClick={clearAll}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    clearAll(event);
                                }
                            }}
                        >
                            <X size={13} />
                        </span>
                    )}
                    <ChevronDown size={15} className="tag-filter-chevron" aria-hidden="true" />
                </span>
            </button>

            {isOpen && (
                <TagFilterMenu
                    tags={tags}
                    selectedTags={selectedTags}
                    onChange={onChange}
                    listId={listId}
                />
            )}
        </div>
    );
};

export default TagFilterDropdown;
